import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useAuth0 } from "@auth0/auth0-react";
import { customCollectionsAPI, CustomCollection } from "../services/api";

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [collections, setCollections] = useState<CustomCollection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Fetch user collections when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setCollectionsLoading(false);
      return;
    }

    const fetchCollections = async () => {
      try {
        const response = await customCollectionsAPI.getCollections(
          getAccessTokenSilently
        );
        setCollections(response.data.collections || []);
      } catch (error) {
        console.error("Failed to fetch collections:", error);
      } finally {
        setCollectionsLoading(false);
      }
    };

    fetchCollections();
  }, [isAuthenticated, getAccessTokenSilently]);

  const features = [
    {
      title: "Continue Practicing",
      description:
        "Lock in your learning with practice exercises from your personalized study decks. Practice exactly what you want.",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
      color: "emerald",
      bgColor: "bg-zinc-100 dark:bg-zinc-800",
      borderColor:
        "border-zinc-300 dark:border-zinc-700 hover:border-teal-700 dark:hover:border-teal-700",
      textColor: "text-teal-600 dark:text-teal-400",
      buttonColor: "bg-teal-600 hover:bg-teal-700",
      route: "/practice",
    },
    {
      title: "Analyze Progress",
      description:
        "View your completion and mastery progress on the passages you've selected for study. Keep up the good work.",
      icon: (
        <svg
          className="w-12 h-12"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
          />
        </svg>
      ),
      color: "indigo",
      bgColor: "bg-zinc-100 dark:bg-zinc-800",
      borderColor:
        "border-zinc-300 dark:border-zinc-700 hover:border-cyan-700 dark:hover:border-cyan-700",
      textColor: "text-cyan-600 dark:text-cyan-400",
      buttonColor: "bg-cyan-600 hover:bg-cyan-700",
      route: "/verbs",
    },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-400 mb-2">
          An interactive learning tool for sharpening your skills in Biblical
          Greek
        </p>
        {user && (
          <p className="text-lg text-cyan-600 dark:text-cyan-400 font-medium">
            Χαῖρε, {capitalizeFirstLetter(user.username)}!
          </p>
        )}
      </div>

      {/* Call to Action Section - Show when user has no collections */}
      {user && !collectionsLoading && collections.length === 0 && (
        <div className="bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-8 md:p-12 border-2 border-purple-300 dark:border-purple-600 hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 mb-16 text-center">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
              Ready to Start Learning?
            </h3>
            <p className="text-lg text-zinc-600 dark:text-zinc-400 mb-8">
              Create your first deck to start your learning journey.
            </p>
            <button
              onClick={() => navigate("/decks")}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg transition-colors"
            >
              Create Your First Deck →
            </button>
          </div>
        </div>
      )}

      {/* Feature Cards */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        {features.map((feature) => (
          <div
            key={feature.title}
            className={`${feature.bgColor} ${feature.borderColor} border-2 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}
          >
            <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
              {feature.title}
            </h2>
            <p className="text-zinc-700 dark:text-zinc-300 text-lg mb-6">
              {feature.description}
            </p>
            <button
              onClick={() => navigate(feature.route)}
              className={`${feature.buttonColor} text-white px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-full`}
            >
              Go →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomePage;
